import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import {
    SelectZoneClassificationDialogOutput,
    SelectZoneClassificationDialogOutputCancel,
    SelectZoneClassificationDialogOutputSuccess
} from '@common/entities/dialog/select-classification-dialog.entity';

import {
    ClassificationService,
    classificationsToFormSelectOptions,
    combineDefaultWithOptions,
    nameIdFormSelectOptions
} from '@mediahaven/shared';
import {
    ActionItem,
    ApiFragment,
    BaseContainer,
    DialogConfig,
    DialogRef,
    FormRadioListItem,
    Identifier,
    isNotNullOrUndefined,
    MediaObjectId,
    MultiFormSelectOptions,
    Zone,
    ZonesService
} from '@stylehaven/components';

@Component({
    standalone: false,
    selector: 'mhv-select-zone-classification-dialog',
    templateUrl: './select-zone-classification-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectZoneClassificationDialogComponent extends BaseContainer implements OnInit {
    public classificationFormSelectOptions$: Observable<MultiFormSelectOptions>;
    public zoneFormSelectOptions$: Observable<MultiFormSelectOptions>;
    public dialogActionItems$: Observable<ActionItem[]>;
    public addToclassificationOptions$: Observable<FormRadioListItem[]>;
    public disableDropdown$: Observable<boolean>;
    public classificationSearchTerm$: ReplaySubject<string> = new ReplaySubject(1);

    public selectZoneClassificationFormGroup: UntypedFormGroup;

    constructor(
        private readonly classificationService: ClassificationService,
        private readonly zoneService: ZonesService,
        public config: DialogConfig,
        private readonly formBuilder: UntypedFormBuilder,
        public dialog: DialogRef<SelectZoneClassificationDialogOutput>
    ) {
        super();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: KeyboardEvent) {
        // Prevent submitting when form is invalid or focus is not on dialog
        if (!this.selectZoneClassificationFormGroup.valid) {
            return;
        }
        event.preventDefault();
        this.submit();
    }

    ngOnInit(): void {
        this.initForm();
        this.initClassificationsStream();
        this.initZonesStream();
    }

    private initForm(): void {
        this.selectZoneClassificationFormGroup = this.formBuilder.group({
            classification: [null, Validators.required],
            zone: [null, Validators.required]
        });
    }

    private initClassificationsStream(): void {
        const searchOptions$: Observable<ApiFragment[]> = this.classificationSearchTerm$.pipe(
            startWith(''),
            distinctUntilChanged(),
            switchMap(searchTerm => this.classificationService.getAllClassifications(searchTerm)),
            shareReplay(1)
        );

        const defaultClassification$: Observable<ApiFragment | null> = this.classificationService
            .getDefaultClassification()
            .pipe(take(1));

        const defaultClassificationId$: Observable<MediaObjectId | null> = defaultClassification$.pipe(
            map(classification => (classification ? classification.getMediaObjectId() : null))
        );

        this.classificationFormSelectOptions$ = combineDefaultWithOptions(
            defaultClassification$,
            searchOptions$,
            (a, b) => a.getRecordId() === b.getRecordId()
        ).pipe(map((classifications: ApiFragment[]) => classificationsToFormSelectOptions(classifications, true)));

        combineLatest([defaultClassificationId$, this.classificationFormSelectOptions$])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(([classificationId, selectOptions]) => {
                this.setInitialClassification(classificationId, selectOptions);
            });
    }

    private setInitialClassification(
        defaultClassification: Identifier | null,
        selectOptions: MultiFormSelectOptions
    ): void {
        if (!this.config.data.classificationId && defaultClassification) {
            return this.classificationControl.setValue([defaultClassification]);
        }

        this.classificationControl.setValue([this.config.data.classificationId]);
        this.classificationControl.disable();
    }

    private initZonesStream(): void {
        this.zoneFormSelectOptions$ = this.zoneService.zones$.pipe(
            map((zones: Zone[]) => nameIdFormSelectOptions(zones))
        );

        this.zoneService
            .getDefaultZone(this.config.data.parentZoneId)
            .pipe(take(1), isNotNullOrUndefined())
            .subscribe(defaultZone => {
                this.setInitialZone(defaultZone);
            });
    }

    private setInitialZone(defaultZone: Zone): void {
        this.zoneControl.setValue([defaultZone.Id]);
    }

    public onClassificationSearchTermChanged(searchTerm: string): void {
        this.classificationSearchTerm$.next(searchTerm);
    }

    submit(): void {
        const classificationValues: string[] = this.classificationControl.value;
        const classificationId: MediaObjectId = classificationValues[0] as MediaObjectId;
        const zoneValues: string[] = this.zoneControl.value;
        const zoneId: string = zoneValues[0];
        const output: SelectZoneClassificationDialogOutputSuccess = {
            type: 'success',
            classificationId,
            zoneId
        };
        this.dialog.close(output);
    }

    cancel(): void {
        const output: SelectZoneClassificationDialogOutputCancel = {
            type: 'cancel'
        };
        this.dialog.close(output);
    }

    get classificationControl(): UntypedFormControl {
        return this.selectZoneClassificationFormGroup.get('classification') as UntypedFormControl;
    }

    get zoneControl(): UntypedFormControl {
        return this.selectZoneClassificationFormGroup.get('zone') as UntypedFormControl;
    }

    ngOnDestroy(): void {
        this.classificationSearchTerm$.complete();
        super.ngOnDestroy();
    }
}
